import { useTranslation } from 'react-i18next';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import Form from 'components/stateless/Form';
import GuideMessage from 'components/stateless/GuideMessage/GuideMessage';
import { BackHeaderTitle } from 'components/stateless/Title/BackHeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { Border } from 'components/templates/section';
import { AUTHORITY_TYPE, COMMON_APPROVAL_TYPE } from 'enums';
import WithProvider from 'utils/render';

import WaitingPartnerAgreementApprovalInfo from './sections/WaitingPartnerAgreementApprovalInfo';
import useWaitingDetailController from './useWaitingDetailController';
import AgreementProvider from '../models/agreement/Provider';
import ExtraInformationProvider from '../models/extraInformation/Context';
import FinancierSettingProvider from '../models/financierSetting/Context';
import DisbursementAccount from '../sections/common/DisbursementAccount';
import DiscountRate from '../sections/common/DiscountRate';
import Financier from '../sections/common/Financier';
import FinancingDuration from '../sections/common/FinancingDuration';
import GeneralInformation from '../sections/common/GeneralInformation';
import LoadAgreement from '../sections/common/LoadAgreement';
import PartnerCompany from '../sections/common/PartnerCompany';
import PlatformPermissionSettings from '../sections/common/PlatformPermissionSettings';
import RepaymentAccountInterest from '../sections/common/RepaymentAccountInterest';
import RepaymentAccountPrincipal from '../sections/common/RepaymentAccountPrincipal';
import SecuredDepositAccount from '../sections/common/SecuredDepositAccount';
import DealerAssociatedAnchorMasterAgreement from '../sections/dealer/AssociatedAnchorMasterAgreement';
import DealerFinancingLimit from '../sections/dealer/FinancingLimit';
import VendorAssociatedAnchorMasterAgreement from '../sections/vendor/AssociatedAnchorMasterAgreement';
import VendorFinancingLimit from '../sections/vendor/FinancingLimit';

const PartnerAgreementWaitingDetail = () => {
  const { t } = useTranslation(['format']);

  const {
    methods,
    isAr,
    isEditable,
    isSameBranch,
    conditionChecker,
    isFirstRegisteredWaitingAgreement,
    handleCancelButtonClick,
    handleChangeRequestButtonClick,
    handleReturnButtonClick,
    handleConfirmButtonClick,
    useAgreementInterface,
    GuideMessageOption,
    getReadOnlyValue,
    isRegistrationRequestButtonDisabled,
    approvalType,
    isEditableStyle,
    isVendorFactoring,
  } = useWaitingDetailController();

  return (
    <>
      <BackHeaderTitle
        title={
          isFirstRegisteredWaitingAgreement
            ? t('text:Partner_Master_Agreement_Registration_Request')
            : t('text:Partner_Master_Agreement_Modification_Request')
        }
      />

      {GuideMessageOption && <GuideMessage {...GuideMessageOption} />}

      {approvalType && <WaitingPartnerAgreementApprovalInfo />}

      <Form
        methods={methods}
        isEditable={isEditable}
        isEditableStyle={isEditableStyle}
        getReadOnlyValue={getReadOnlyValue}
      >
        {useAgreementInterface && isEditable && (
          <div className="content-area">
            <SectionTitle title={t('text:Load_Agreement_from_Financier_System')} />
            <LoadAgreement />
          </div>
        )}

        <div className="content-area">
          <SectionTitle title={t('text:Contracting_Parties')} />
          <Border isEditable={isEditableStyle}>
            <Financier />
            <PartnerCompany />
            {isAr ? <VendorAssociatedAnchorMasterAgreement /> : <DealerAssociatedAnchorMasterAgreement />}
          </Border>
        </div>
        <div className="content-area">
          <SectionTitle title={t('text:General_Information')} />
          <Border isEditable={isEditableStyle}>
            <GeneralInformation />
          </Border>
        </div>
        <div className="content-area">
          <SectionTitle title={t('text:Financing_Terms_and_Conditions')} />
          <Border isEditable={isEditableStyle}>
            {isAr ? <VendorFinancingLimit /> : <DealerFinancingLimit />}
            <DiscountRate isVendorFactoring={isVendorFactoring} />
            <FinancingDuration isVendorFactoring={isVendorFactoring} />
            <PlatformPermissionSettings isVendorFactoring={isVendorFactoring} />
          </Border>
        </div>
        <div className="content-area">
          <SectionTitle title={t('text:Designated Account')} />
          <Border isEditable={isEditableStyle}>
            <DisbursementAccount isVendorFactoring={isVendorFactoring} />
            <RepaymentAccountPrincipal />
            {!isVendorFactoring && (
              <>
                <RepaymentAccountInterest />
                <SecuredDepositAccount />
              </>
            )}
          </Border>
        </div>
      </Form>

      {/* button */}
      {isSameBranch && (
        <div className="content-area">
          <div className="flex-end">
            {conditionChecker(AUTHORITY_TYPE.OPERATOR, COMMON_APPROVAL_TYPE.AUTHORIZER_RETURNED) && (
              <>
                <Button
                  size={ButtonSizeEnum.LG}
                  onClick={handleCancelButtonClick}
                  color={ButtonColorEnum.SECONDARY}
                  variant={ButtonVariantEnum.OUTLINED}
                >
                  {t('text:Cancel')}
                </Button>
                <Button
                  size={ButtonSizeEnum.LG}
                  onClick={handleChangeRequestButtonClick}
                  className="ms-2"
                  disabled={isRegistrationRequestButtonDisabled}
                >
                  {isFirstRegisteredWaitingAgreement ? t('text:Registration_Request') : t('text:Request_Change')}
                </Button>
              </>
            )}
            {conditionChecker(AUTHORITY_TYPE.AUTHORIZER, COMMON_APPROVAL_TYPE.OPERATOR_REQUEST) && (
              <>
                <Button
                  size={ButtonSizeEnum.LG}
                  onClick={handleReturnButtonClick}
                  color={ButtonColorEnum.RED}
                  variant={ButtonVariantEnum.OUTLINED}
                >
                  {t('text:Revert')}
                </Button>
                <Button size={ButtonSizeEnum.LG} onClick={handleConfirmButtonClick} className="ms-2">
                  {t('text:Confirm')}
                </Button>
              </>
            )}
            {conditionChecker(AUTHORITY_TYPE.OPERATOR, COMMON_APPROVAL_TYPE.OPERATOR_REQUEST) && (
              <Button
                size={ButtonSizeEnum.LG}
                onClick={handleCancelButtonClick}
                color={ButtonColorEnum.SECONDARY}
                variant={ButtonVariantEnum.OUTLINED}
              >
                {t('text:Cancel_Request')}
              </Button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WithProvider({
  Component: PartnerAgreementWaitingDetail,
  Provider: [ExtraInformationProvider, FinancierSettingProvider, AgreementProvider],
});
